import { useState, useEffect, useCallback } from 'react';
import { leadsAPI } from '../../lib/api';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

export default function ReportsTab({ caps }) {
  const [scope, setScope] = useState(caps.is_manager ? 'team' : (caps.can_view_all_reports ? 'all' : 'mine'));
  const [summary, setSummary] = useState(null);
  const [team, setTeam] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [activity, setActivity] = useState(null);
  const [sources, setSources] = useState(null);
  const [dataHealth, setDataHealth] = useState(null);

  const load = useCallback(async (s) => {
    try {
      const sumRes = await leadsAPI.reportsSummary(s);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reports');
      return;
    }
    (s === 'mine' ? Promise.resolve({ data: { members: [] } }) : leadsAPI.reportsTeam(s)).then(r => setTeam(r.data.members)).catch(() => setTeam([]));
    leadsAPI.reportsFunnel(s).then(r => setFunnel(r.data.funnel)).catch(() => setFunnel([]));
    leadsAPI.reportsOverdue(s).then(r => setOverdue(r.data)).catch(() => setOverdue(null));
    leadsAPI.reportsConversions(s).then(r => setConversions(r.data.conversions)).catch(() => setConversions([]));
    leadsAPI.reportsActivity(s, 7).then(r => setActivity(r.data.rows)).catch(() => setActivity([]));
    if (caps.can_view_all_reports) {
      leadsAPI.reportsSources().then(r => setSources(r.data.sources)).catch(() => {});
    }
    if (caps.can_admin) {
      leadsAPI.reportsDataHealth().then(r => setDataHealth(r.data)).catch(() => {});
    }
  }, [caps.can_view_all_reports, caps.can_admin]);

  useEffect(() => { load(scope); }, [scope, load]);

  if (!summary) return <div className="mt-4 text-muted-foreground" data-testid="leads-reports-loading">Loading reports...</div>;

  return (
    <div className="space-y-4 mt-4" data-testid="leads-reports-tab">
      <Select value={scope} onValueChange={setScope}>
        <SelectTrigger className="h-9 w-40" data-testid="leads-reports-scope-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="mine">My leads</SelectItem>
          {caps.is_manager && <SelectItem value="team">My team</SelectItem>}
          {caps.can_view_all_reports && <SelectItem value="all">All leads</SelectItem>}
        </SelectContent>
      </Select>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="leads-reports-total"><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{summary.total}</p></Card>
        <Card className="p-4" data-testid="leads-reports-conversion"><p className="text-xs text-muted-foreground">Conversion rate</p><p className="text-2xl font-bold">{summary.conversion_rate_pct}%</p></Card>
        <Card className="p-4" data-testid="leads-reports-overdue"><p className="text-xs text-muted-foreground">Overdue follow-ups</p><p className="text-2xl font-bold">{summary.overdue_follow_ups}</p></Card>
        <Card className="p-4" data-testid="leads-reports-avg-contact"><p className="text-xs text-muted-foreground">Avg days to first contact</p><p className="text-2xl font-bold">{summary.avg_days_to_first_contact ?? '—'}</p></Card>
      </div>

      <Card className="p-4" data-testid="leads-reports-by-status">
        <h3 className="font-semibold mb-2">By status</h3>
        <div className="flex gap-4 flex-wrap text-sm">
          {Object.entries(summary.by_status).map(([k, v]) => <span key={k}>{k}: <b>{v}</b></span>)}
        </div>
      </Card>

      {funnel && (
        <Card className="p-4" data-testid="leads-reports-funnel">
          <h3 className="font-semibold mb-2">Funnel</h3>
          {funnel.length > 0 ? (
            <div className="flex gap-4 flex-wrap text-sm">
              {funnel.map(f => <span key={f.status}>{f.status}: <b>{f.count}</b></span>)}
            </div>
          ) : <p className="text-sm text-muted-foreground">No leads in this scope yet</p>}
        </Card>
      )}

      {activity && (
        <Card className="p-4" data-testid="leads-reports-activity">
          <h3 className="font-semibold mb-2">Activity (last 7 days)</h3>
          {activity.length > 0 ? (
            <div className="divide-y">
              {activity.map((a, i) => (
                <div key={i} className="py-1 text-sm flex items-center justify-between" data-testid={`leads-activity-row-${i}`}>
                  <span>{a.name || a.user_id} · {a.day}</span>
                  <span className="text-muted-foreground">{a.count} activities</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No activity logged in the last 7 days</p>}
        </Card>
      )}

      {overdue && (
        <Card className="p-4" data-testid="leads-reports-overdue-detail">
          <h3 className="font-semibold mb-2">Overdue follow-ups by owner ({overdue.total})</h3>
          {overdue.by_owner.length > 0 ? (
            <div className="flex gap-4 flex-wrap text-sm">
              {overdue.by_owner.map(o => <span key={o.owner_id}>{o.owner_name || o.owner_id}: <b>{o.count}</b></span>)}
            </div>
          ) : <p className="text-sm text-muted-foreground">No overdue follow-ups</p>}
        </Card>
      )}

      {conversions && (
        <Card className="p-4" data-testid="leads-reports-conversions">
          <h3 className="font-semibold mb-2">Recent conversions</h3>
          {conversions.length > 0 ? (
            <div className="divide-y">
              {conversions.slice(0, 20).map(c => (
                <div key={c.id} className="py-1 text-sm flex items-center justify-between" data-testid={`leads-conversion-row-${c.id}`}>
                  <span>{c.code} · {c.company}</span>
                  <span className="text-muted-foreground">{c.converted_to?.type?.toLowerCase()} · {c.days_to_convert != null ? `${c.days_to_convert}d` : '—'}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No conversions in this scope yet</p>}
        </Card>
      )}

      {team && team.length > 0 && (
        <Card className="p-4" data-testid="leads-reports-team-table">
          <h3 className="font-semibold mb-2">Team</h3>
          <div className="divide-y">
            {team.map(m => (
              <div key={m.user_id} className="py-2 text-sm flex items-center justify-between" data-testid={`leads-team-row-${m.user_id}`}>
                <span>{m.name || m.email}</span>
                <span className="text-muted-foreground">total {m.total} · open {m.open} · converted {m.converted} · overdue {m.overdue}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {sources && sources.length > 0 && (
        <Card className="p-4" data-testid="leads-reports-sources">
          <h3 className="font-semibold mb-2">By source <span className="text-xs font-normal text-muted-foreground">(all leads, not scope-filtered)</span></h3>
          <div className="flex gap-4 flex-wrap text-sm">
            {sources.map(s => <span key={s.source}>{s.source}: <b>{s.total}</b> ({s.converted} converted)</span>)}
          </div>
        </Card>
      )}

      {dataHealth && (
        <Card className="p-4" data-testid="leads-reports-data-health">
          <h3 className="font-semibold mb-2">Data health <span className="text-xs font-normal text-muted-foreground">(all leads, not scope-filtered)</span></h3>
          <div className="flex gap-4 flex-wrap text-sm">
            <span>Total: <b>{dataHealth.total}</b></span>
            <span>Missing email: <b>{dataHealth.missing_email}</b></span>
            <span>Missing phone/mobile: <b>{dataHealth.missing_phone_and_mobile}</b></span>
            <span>Missing country: <b>{dataHealth.missing_country}</b></span>
            <span>Unassigned: <b>{dataHealth.unassigned}</b></span>
          </div>
        </Card>
      )}
    </div>
  );
}
